import { Alert, Link, Stack } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useGetPreviewConceptsQuery,
  useGetPreviewCreatedGroupsQuery,
} from '@verantos-dev/data-access-concept-groups';
import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';

import { ConceptGroupDetailsDialog } from './ConceptGroupDetailsDialog/ConceptGroupDetailsDialog';
import { useValidationResultsGrid } from '../hooks/useValidationResultsGrid';

interface PreviewValidationResultsGridProps {
  previewId: string;
}

/**
 * Server-side paginated grid for browsing preview concepts.
 * Uses the shared DataGrid in server mode with the preview REST API.
 */
export function PreviewValidationResultsGrid({ previewId }: PreviewValidationResultsGridProps) {
  const { columns } = useValidationResultsGrid();
  const { formatMessage } = useIntl();
  const [detailsGroupId, setDetailsGroupId] = useState<string | null>(null);

  const { data: createdGroups } = useGetPreviewCreatedGroupsQuery({ previewId });

  // Build the fetch hook that the DataGrid expects for server mode.
  // DataGrid passes searchParams (page, pageSize, sort, filter) — forward them directly.
  const useFetchPreviewData = (searchParams?: string) => {
    return useGetPreviewConceptsQuery({ previewId, searchParams });
  };

  return (
    <>
      <Stack spacing={1}>
        {createdGroups && createdGroups.length > 0 && (
          <Alert severity="info" data-testid="preview-created-groups-banner">
            {formatMessage({ id: 'conceptGroups.preview.previouslyCreated' })}{' '}
            {createdGroups.map((group, idx) => (
              <span key={group.id}>
                {idx > 0 && ', '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setDetailsGroupId(group.id)}
                  data-testid={`preview-created-group-link-${group.id}`}
                >
                  {group.name}
                </Link>
              </span>
            ))}
          </Alert>
        )}
        <DataGridLayout id="preview-validation-results-datagrid-layout" sx={{ maxHeight: '450px' }}>
          <DataGrid
            id="preview-validation-results"
            mode="server"
            disableSearchParamSync
            preventUrlReadWhenSyncDisabled={true}
            getRowId={(row) => row.conceptId}
            columns={columns}
            useFetchDataGridData={useFetchPreviewData}
            getRowCount={(data) => data.pagination?.total ?? 0}
            pagination
            loadOnFetch
            initialPageSize={100}
            pageSizeOptions={[100]}
            sx={{
              '& .MuiDataGrid-row': {
                '--rowBorderColor': theme.palette.divider,
              },
            }}
          />
        </DataGridLayout>
      </Stack>

      {detailsGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={detailsGroupId}
          onClose={() => setDetailsGroupId(null)}
        />
      )}
    </>
  );
}
